import { useNoshStore } from './store';
import { uploadImageToStorage, recognizeImage, getSignedImageUrl, optimizeImage } from './api';
import { supabase } from './supabase';

// Global image scanning handler that persists across page navigation
export class GlobalImageScanner {
  private static instance: GlobalImageScanner;

  private constructor() {}

  public static getInstance(): GlobalImageScanner {
    if (!GlobalImageScanner.instance) {
      GlobalImageScanner.instance = new GlobalImageScanner();
    }
    return GlobalImageScanner.instance;
  }

  public async startScan(
    file: File, 
    scanType: 'inventory' | 'meal' = 'inventory',
    onComplete?: (result: any) => void,
    onError?: (error: string) => void
  ): Promise<void> {
    const store = useNoshStore.getState();
    let displayImagePath: string | null = null; // Move to outer scope for error handling
    
    try {
      console.log('🚀 [Global Scanner] Starting image scan:', {
        scanType,
        fileName: file.name,
        fileSize: Math.round(file.size / 1024) + 'KB'
      });

      // FIRST: Check usage limits before any expensive operations
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) throw new Error('User not authenticated');
        
        const { AIUsageService } = await import('./aiUsageService');
        const featureType = scanType === 'meal' ? 'meal_analysis' : 'image_scan';
        console.log(`🛡️ [Global Scanner] Checking ${featureType} usage limits before processing`);
        
        const limits = await AIUsageService.checkFeatureLimit(userId, featureType);
        if (!limits.can_use) {
          console.warn(`🚫 [Global Scanner] ${scanType === 'meal' ? 'Meal analysis' : 'Image scan'} blocked by usage limits`);
          
          // Show global upgrade modal
          store.showUsageLimitModal(limits, featureType);
          
          // Call completion handler with limits error to prevent empty modals
          if (onComplete) {
            onComplete({ 
              success: false,
              error: 'Usage limit exceeded', 
              limits,
              requiresUpgrade: true 
            });
          }
          
          return;
        }
        console.log(`✅ [Global Scanner] Usage limits OK, proceeding with ${featureType}`);
      } catch (limitError) {
        console.warn('⚠️ [Global Scanner] Usage limit check failed, proceeding anyway:', limitError);
      }

      // Initialize global progress state
      store.startImageRecognition(scanType);

      // Step 1: Optimize the image with scan-type-specific settings
      store.updateImageRecognitionProgress('optimizing', 0);
      
      // Check if image is already optimized (small size indicates prior optimization)
      const fileSizeKB = file.size / 1024;
      const isAlreadyOptimized = fileSizeKB <= 500; // If already small, likely pre-optimized
      
      let optimizedFile: File;
      
      if (isAlreadyOptimized) {
        console.log('📷 [Global Scanner] Image already optimized, skipping redundant compression:', {
          currentSize: Math.round(fileSizeKB) + 'KB',
          fileName: file.name
        });
        optimizedFile = file; // Use original file as it's already optimized
      } else {
        // Use proper optimization parameters based on scan type
        const purpose = scanType === 'meal' ? 'ai-analysis' : 'general';
        const maxSizeKB = scanType === 'meal' ? 1000 : 800; // Allow larger files for meal analysis
        const quality = scanType === 'meal' ? 0.85 : 0.8; // Higher quality for meal analysis
        
        console.log('📷 [Global Scanner] Optimizing image with settings:', {
          scanType,
          purpose,
          maxSizeKB,
          quality,
          currentSize: Math.round(fileSizeKB) + 'KB'
        });
        
        optimizedFile = await optimizeImage(file, maxSizeKB, quality, 'camera', purpose);
        
        console.log('✅ [Global Scanner] Image optimized successfully:', {
          originalSize: Math.round(file.size / 1024) + 'KB',
          optimizedSize: Math.round(optimizedFile.size / 1024) + 'KB',
          reduction: Math.round(((file.size - optimizedFile.size) / file.size) * 100) + '%'
        });
      }

      // Step 2: Dual upload for meals (AI analysis + display versions)
      store.updateImageRecognitionProgress('uploading', 0);
      
      // Get the authenticated user ID from the session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('User not authenticated');
      
      let imagePath: string;
      
      if (scanType === 'meal') {
        console.log('📷 [Meal Scanner] Creating dual versions: AI analysis + display');
        
        // Upload high-res version for AI analysis
        imagePath = await uploadImageToStorage(optimizedFile, userId);
        console.log('✅ [Meal Scanner] High-res image uploaded for AI analysis:', imagePath);
        
        // Create 500x500 display version
        console.log('📷 [Meal Scanner] Creating 500x500 display version...');
        const displayFile = await optimizeImage(file, 150, 0.8, 'camera', 'display');
        
        // Upload display version with _display suffix
        const displayFileName = `${userId}/${Date.now()}_display.jpg`;
        const { data: displayData, error: displayError } = await supabase.storage
          .from('images')
          .upload(displayFileName, displayFile, {
            contentType: 'image/jpeg',
            upsert: false
          });
        
        if (displayError) {
          console.warn('⚠️ [Meal Scanner] Failed to upload display version, using AI version:', displayError);
          displayImagePath = imagePath; // Fallback to AI version
        } else {
          displayImagePath = displayData.path;
          console.log('✅ [Meal Scanner] Display image uploaded:', displayImagePath);
        }
      } else {
        // Standard upload for inventory scans
        imagePath = await uploadImageToStorage(optimizedFile, userId);
        console.log('✅ [Global Scanner] Image uploaded to storage:', imagePath);
      }
      
      // Store image path in global state for potential cleanup
      store.setImageRecognitionPath(imagePath);

      // Step 3: Generate signed URL for AI processing (needed for both types)
      store.updateImageRecognitionProgress('processing', 0);
      let signedUrl: string;
      
      try {
        // Generate signed URL for AI processing - needed for both meal and inventory
        signedUrl = await getSignedImageUrl(imagePath, 300); // 5 minute expiry
        console.log(`✅ [Global Scanner] Signed URL generated for ${scanType} scan (AI processing)`);
      } catch (error) {
        console.error('❌ [Global Scanner] Failed to generate signed URL:', error);
        throw new Error('Failed to generate signed URL for image processing');
      }

      // Step 4: Call recognition API with global progress updates
      store.updateImageRecognitionProgress('analyzing', 0);
      const result = await recognizeImage(signedUrl, scanType, (phase, elapsed) => {
        store.updateImageRecognitionProgress(phase, elapsed);
      });

      if (result) {
        console.log('✅ [Global Scanner] Image recognition completed:', result);
        
        // Complete the global progress
        store.completeImageRecognition();
        
        // Schedule cleanup for different scan types
        if (scanType === 'inventory') {
          console.log('🧹 [Global Scanner] Scheduling inventory image cleanup');
          this.scheduleImageCleanup(imagePath);
        } else if (scanType === 'meal' && displayImagePath && imagePath !== displayImagePath) {
          // For meals, clean up the AI analysis image but keep the display image
          console.log('🧹 [Meal Scanner] Scheduling AI analysis image cleanup (keeping display image)');
          this.scheduleImageCleanup(imagePath);
        }
        
        // Call completion handler with enhanced result including image info
        if (onComplete) {
          let finalImageUrl = '';
          let finalImagePath = '';
          
          if (scanType === 'meal') {
            // For meals, use display image URL for storage, but we need signed URL
            if (displayImagePath) {
              try {
                finalImageUrl = await getSignedImageUrl(displayImagePath, 86400); // 24 hour expiry for display
                finalImagePath = displayImagePath;
                console.log('✅ [Meal Scanner] Using display image for meal storage:', finalImageUrl);
              } catch (error) {
                console.warn('⚠️ [Meal Scanner] Failed to get display signed URL, using AI URL:', error);
                finalImageUrl = signedUrl;
                finalImagePath = imagePath;
              }
            } else {
              finalImageUrl = signedUrl;
              finalImagePath = imagePath;
            }
          }
          
          const enhancedResult = {
            ...result,
            imageUrl: finalImageUrl,
            imagePath: finalImagePath,
            uploadedAt: new Date().toISOString()
          };
          console.log('📤 [Global Scanner] Calling completion handler with enhanced result:', enhancedResult);
          onComplete(enhancedResult);
        }
        
        return result;
      } else {
        throw new Error('No result received from image recognition');
      }
    } catch (error: any) {
      console.error('❌ [Global Scanner] Scan failed:', error);
      
      // Clean up images even on failure
      const currentPath = store.imageRecognition.imagePath;
      if (currentPath) {
        if (scanType === 'inventory') {
          console.log('🧹 [Global Scanner] Cleaning up inventory image after error');
          this.scheduleImageCleanup(currentPath);
        } else if (scanType === 'meal') {
          console.log('🧹 [Meal Scanner] Cleaning up meal images after error');
          this.scheduleImageCleanup(currentPath);
          // Also clean up display image if it was created
          if (displayImagePath && displayImagePath !== currentPath) {
            this.scheduleImageCleanup(displayImagePath);
          }
        }
      }
      
      // Set error in global state
      store.setImageRecognitionError(error.message);
      
      // Call error handler
      if (onError) {
        onError(error.message);
      }
      
      // Hide modal after a delay to show error
      setTimeout(() => {
        store.completeImageRecognition();
      }, 3000);
      
      throw error;
    }
  }

  public cancel(): void {
    const store = useNoshStore.getState();
    console.log('🚫 [Global Scanner] Cancelling image scan');
    
    // Note: We could add actual network request cancellation here if needed
    // For now, we just clean up the UI state
    store.cancelImageRecognition();
  }

  public isActive(): boolean {
    const store = useNoshStore.getState();
    return store.imageRecognition.isActive;
  }

  public getCurrentScanType(): 'inventory' | 'meal' {
    const store = useNoshStore.getState();
    return store.imageRecognition.scanType;
  }

  private async scheduleImageCleanup(imagePath: string): Promise<void> {
    // Schedule image deletion after a delay to ensure processing is complete
    // Use longer delay for better reliability across different network conditions
    setTimeout(async () => {
      try {
        console.log('🧹 [Global Scanner] Attempting to delete inventory image:', imagePath);
        
        // Delete the image from storage
        const { error } = await supabase.storage
          .from('images')
          .remove([imagePath]);
        
        if (error) {
          console.warn('⚠️ [Global Scanner] Failed to delete inventory image:', error.message);
          
          // Retry once after 30 seconds if initial deletion failed
          setTimeout(async () => {
            try {
              console.log('🔄 [Global Scanner] Retrying inventory image deletion:', imagePath);
              const { error: retryError } = await supabase.storage
                .from('images')
                .remove([imagePath]);
              
              if (retryError) {
                console.warn('⚠️ [Global Scanner] Retry deletion failed:', retryError.message);
              } else {
                console.log('✅ [Global Scanner] Inventory image deleted successfully on retry');
              }
            } catch (retryErr) {
              console.warn('⚠️ [Global Scanner] Error during retry deletion:', retryErr);
            }
          }, 30000); // Retry after 30 seconds
        } else {
          console.log('✅ [Global Scanner] Inventory image deleted successfully');
        }
      } catch (error) {
        console.warn('⚠️ [Global Scanner] Error during image cleanup:', error);
        
        // Don't block the app if cleanup fails - just log it
        // The manual cleanup function can handle orphaned images later
      }
    }, 20000); // Increased from 10 to 20 seconds for better reliability
  }
}

// Singleton instance
export const globalImageScanner = GlobalImageScanner.getInstance();

// Utility hooks for components
export const useGlobalImageScanner = () => {
  const store = useNoshStore();

  return {
    isScanning: store.imageRecognition.isActive,
    scanType: store.imageRecognition.scanType,
    currentPhase: store.imageRecognition.currentPhase,
    elapsedTime: store.imageRecognition.elapsedTime,
    error: store.imageRecognition.error,
    startScan: globalImageScanner.startScan.bind(globalImageScanner),
    cancel: globalImageScanner.cancel.bind(globalImageScanner),
  };
}; 